import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { cloneDeep } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDollarSign } from '@fortawesome/pro-light-svg-icons';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { TextField, InputAdornment, CircularProgress } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteForever';
import { ConfirmationDialog } from 'shared/components';
import { SectionBase } from './SectionBase';
import ForecastSummary from './IndicatorsForecastSummaryTable';
import ForecastDetails from './IndicatorsForecastDetailsTable';
import { RadioGroup } from './RadioGroup';
import MeasurementSelector from './MeasurementSelector';
import LessonSelector from './LessonSelector';
import { QuickDrawer } from 'shared/components';
import { Button } from '../styles';
import InKindHelpDrawer from './InKindHelpDrawer';
import HelpOverheadDrawer from './HelpOverheadDrawer';
import { API_URL } from 'shared/utils/constants';
import { getAuthorizationHeader } from 'shared/utils';

const IMAGE_FILES = ['JPG', 'JPEG', 'PNG'];

export const SectionCustomOptions = ({
  title,
  subTitle,
  helpText,
  error,
  questionId,
  value,
  onChange,
  options,
  handleBack,
  reportId,
  extraData,
  guidanceText,
  feExtraRequirement,
}) => {
  const [reload, setReload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [invalidFile, setInvalidFile] = useState(false);
  const [thumb, setThumb] = useState(value?.text?.url);
  const [showDialogDeletePicture, setShowDialogDeletePicture] = useState(false);
  const [fileOptions, setFileOptions] = useState();
  const [openHelpBudgetDrawer, setOpenHelpBudgetDrawer] = useState(false);
  const [openHelpInKindDrawer, setOpenHelpInKindDrawer] = useState(false);
  const [openHelpOverheadDrawer, setOpenHelpOverheadDrawer] = useState(false);

  // effects
  useEffect(() => {
    if (!axios.defaults.headers.common['ti-id-token']) {
      const token = getAuthorizationHeader();
      axios.defaults.headers.common['ti-id-token'] = token;
    }
  }, []);

  useEffect(() => {
    if (value === undefined) {
      setReload(true);
      setTimeout(() => {
        setReload(false);
      }, 0);
    }
  }, [value]);

  useEffect(() => {
    if (value?.text?.url) {
      const url = value?.text?.url || '';
      const isImage = IMAGE_FILES.reduce((acc, f) => {
        const source = url.toUpperCase();
        if (source.endsWith(f)) acc = true;
        return acc;
      }, false);
      const newThumb = isImage ? value?.text?.url : 'other';
      setThumb(newThumb);
    }
  }, [value]);

  // methods
  const uploadPhoto = async file => {
    if (!file) return;
    const VALID = ['JPG', 'JPEG', 'PNG', 'MP4', 'DOC', 'DOCX', 'PDF'];
    const isValid = VALID.reduce((acc, f) => {
      const source = file.type.toUpperCase() || file.name.toUpperCase();
      if (source.endsWith(f)) acc = true;
      return acc;
    }, false);
    const isImage = IMAGE_FILES.reduce((acc, f) => {
      const source = file.type.toUpperCase() || file.name.toUpperCase();
      if (source.endsWith(f)) acc = true;
      return acc;
    }, false);

    if (isValid) {
      const newThumb = isImage ? URL.createObjectURL(file) : 'other';
      setThumb(newThumb);
      setInvalidFile();
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      try {
        const response = await axios({
          method: 'post',
          url: `${API_URL}/v4/reports/${reportId}/story_file`,
          data: formData,
          headers: { 'Content-Type': 'multipart/form-data', 'ti-id-token': getAuthorizationHeader() },
        });
        const {
          id,
          attributes: { url },
        } = response?.data?.data;
        const picture = { id, url };
        setUploading(false);
        return picture;
      } catch (error) {
        setInvalidFile(
          'Invalid file. Accepted file formats: JPG, PNG, MP4, DOC, PDF.',
        );
        setUploading(false);
        return false;
      }
    } else {
      setInvalidFile(
        'Invalid file. Accepted file formats: JPG, PNG, MP4, DOC, PDF.',
      );
    }
  };

  const handleChange = useCallback(
    async (answerId, text, type) => {
      const newValue = { ...cloneDeep(value), answerId, type };
      if (type === 'upload_photo') {
        const picture = await uploadPhoto(text);
        if (picture) {
          newValue.text = {
            ...cloneDeep(newValue.text),
            fileId: picture.id,
            url: picture.url,
          };
        }
        onChange(questionId, newValue);
        return;
      }
      if (type === 'currency_multi') {
        newValue.text = {
          ...cloneDeep(newValue.text),
          currency: text,
        };
        onChange(questionId, newValue);
        return;
      }
      if (type === 'long_text_multi') {
        newValue.text = { ...cloneDeep(newValue.text), text };
        onChange(questionId, newValue);
        return;
      }
      if (type === 'categories_lesson') {
        newValue.text = { ...cloneDeep(newValue.text), lesson: text };
        onChange(questionId, newValue);
        return;
      }
      /* file type */
      if (type === 'file') {
        let fileUploaded = null;
        setInvalidFile(false);
        setUploading(true);
        const formData = new FormData();
        formData.append('file', text);
        try {
          const response = await axios({
            method: 'post',
            url: `${API_URL}/v4/reports/${reportId}/result_files`,
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          const { id, attributes } = response?.data?.data;
          fileUploaded = `${attributes?.name} File: ${id}`;
        } catch (error) {
          setInvalidFile(
            'Invalid file. Accepted file formats: DOC, DOCX, ODT, PDF, XLS, XLSX, ODS, PPT, PPTX, TXT.',
          );
          console.warn(error);
        }
        setUploading(false);
        newValue.text = fileUploaded;
      }
      /* file type END */
      if (text !== undefined && type !== 'file') {
        newValue.text = text;
      }
      onChange(questionId, newValue);
    },
    [value, onChange, questionId, reportId],
  );

  const handleClearThumb = async (answerId, type) => {
    setShowDialogDeletePicture(true);
    setFileOptions({ answerId, type });
  };

  const handleCloseDeletePicture = () => {
    setShowDialogDeletePicture(false);
    setFileOptions();
  };

  const handleSetError = error => {
    setInvalidFile(error);
  };

  const handleConfirmDeletePicture = async () => {
    const { answerId, type } = fileOptions;
    setThumb();
    setInvalidFile();
    setShowDialogDeletePicture(false);
    document.getElementById('input_upload_photo').value = '';
    const newValue = { ...cloneDeep(value), answerId, type };
    newValue.text = {
      ...cloneDeep(newValue.text),
      fileId: undefined,
      url: undefined,
    };
    setFileOptions();
    onChange(questionId, newValue);
    try {
      const response = await axios({
        method: 'delete',
        url: `${API_URL}/v4/reports/${reportId}/story_file`,
      });
    } catch (error) { /* empty */ }
  };

  const handleHelpBudget = () => {
    setOpenHelpBudgetDrawer(!openHelpBudgetDrawer);
  };

  const handleInKindDrawerPress = () => {
    setOpenHelpInKindDrawer(!openHelpInKindDrawer);
  };

  const handleCompleteKindDrawer = amount => {
    const { id, responseType: type } = options?.[0];
    handleChange(id, amount, type);
    setOpenHelpInKindDrawer(!openHelpInKindDrawer);
  };

  const handleOverheadDrawerPress = () => {
    setOpenHelpOverheadDrawer(!openHelpOverheadDrawer);
  };

  const handleClearFile = async clearId => {
    onChange(clearId, undefined);
    try {
      const response = await axios({
        method: 'delete',
        url: `${API_URL}/v4/reports/${reportId}/result_files`,
      });
    } catch (error) { /* empty */ }
  };

  return (
    <SectionBase {...{ title, subTitle, helpText, guidanceText, questionId }}>
      {!reload &&
        options.map(({ responseType: type, id, response }) => (
          <RenderOption
            {...{
              type,
              handleChange,
              value,
              id,
              response,
              handleBack,
              uploading,
              questionId,
              extraData,
              invalidFile,
              thumb,
              handleClearThumb,
              handleSetError,
              feExtraRequirement,
              handleHelpBudget,
              handleInKindDrawerPress,
              handleOverheadDrawerPress,
              handleClearFile,
            }}
          />
        ))}
      <ConfirmationDialog
        openDialog={showDialogDeletePicture}
        onCloseDialog={handleCloseDeletePicture}
        actionButtonOne={handleConfirmDeletePicture}
        actionButtonTwo={handleCloseDeletePicture}
        textButtonOne="Yes"
        textButtonTwo="No"
        title="Are you sure you want to delete the photo?"
        header="Confirmation is required"
      />
      {feExtraRequirement === 'help_in_kind' && (
        <InKindHelpDrawer
          open={openHelpInKindDrawer}
          onClose={handleInKindDrawerPress}
          onComplete={handleCompleteKindDrawer}
        />
      )}
      {feExtraRequirement === 'help_overhead' && (
        <HelpOverheadDrawer
          open={openHelpOverheadDrawer}
          onClose={handleOverheadDrawerPress}
        />
      )}
      {feExtraRequirement === 'help_budget' && (
        <QuickDrawer
          width="auto"
          title="Example of the expected direct cost of a program."
          open={openHelpBudgetDrawer}
          onClose={handleHelpBudget}
        >
          <DrawerContainer>
            <span>
              Use the table below to calculate the direct cost of this program
            </span>
            <div class="t">
              <div class="r title">
                <div class="c">Category</div>
                <div class="c">Value</div>
              </div>
              <div class="r first">
                <div class="c">Salaries</div>
                <div class="c">$5,000</div>
              </div>
              <div class="r">
                <div class="c">Equipment</div>
                <div class="c">$5,000</div>
              </div>
              <div class="r">
                <div class="c">Material, Supplies</div>
                <div class="c">$5,000</div>
              </div>
              <div class="r">
                <div class="c">Telephone</div>
                <div class="c">$5,000</div>
              </div>
              <div class="r">
                <div class="c">Travel</div>
                <div class="c">$5,000</div>
              </div>
              <div class="r">
                <div class="c">Proffesional services</div>
                <div class="c">$5,000</div>
              </div>
              <div class="r">
                <div class="c">Miscellaneous</div>
                <div class="c">$5,000</div>
              </div>
              <div class="r last">
                <div class="c">Total value</div>
                <div class="c">$35,000</div>
              </div>
            </div>
            <ButtonDrawer onClick={handleHelpBudget}>Got it</ButtonDrawer>
          </DrawerContainer>
        </QuickDrawer>
      )}
    </SectionBase>
  );
};

const RenderOption = ({
  type,
  handleChange,
  value,
  id,
  response,
  handleBack,
  uploading,
  questionId,
  extraData,
  invalidFile,
  thumb,
  handleClearThumb,
  handleSetError,
  feExtraRequirement,
  handleHelpBudget,
  handleInKindDrawerPress,
  handleOverheadDrawerPress,
  handleClearFile,
}) => {
  let component = null;
  const initialValue = typeof value === 'string' ? value : value?.text;

  /* load initial value in object format for required types */
  useEffect(() => {
    if (type === 'long_text' && typeof value === 'string') {
      handleChange(id, value, type);
    }
  }, [id, value, type, handleChange]);

  const linkType = questionId === 50013 && initialValue ? 'measured' : type;

  switch (type) {
    case 'number':
      component = (
        <NumberField
          value={initialValue}
          onChange={ev => handleChange(id, ev.target.value, type)}
        />
      );
      break;
    case 'link':
      component = (
        <p onClick={() => handleChange(id, initialValue, linkType)}>
          <Link>{response}</Link>
        </p>
      );
      break;
    case 'link_back':
      component = (
        <p onClick={handleBack}>
          <Link>{response}</Link>
        </p>
      );
      break;
    case 'link_confirm':
      if (value) return null;
      component = (
        <p onClick={() => handleChange(id, initialValue, linkType)}>
          <Link>{response}</Link>
        </p>
      );
      break;
    case 'forecast_summary':
      component = (
        <ForecastSummary
          reportId={extraData?.reportId}
          beneficiary={extraData?.beneficiary}
        />
      );
      break;
    case 'forecast_details':
      component = (
        <ForecastDetails
          reportId={extraData?.reportId}
          beneficiary={extraData?.beneficiary}
        />
      );
      break;
    case 'long_text':
      component = (
        <TextArea
          placeholder={response}
          value={initialValue}
          onChange={ev => handleChange(id, ev.target.value, type)}
        />
      );
      break;
    case 'choice': {
      let choices = [];
      try {
        choices = JSON.parse(response);
      } catch {
        console.warn(
          'Warning: Question with type choices, the choices are not JSON parseable',
        );
      }
      component = (
        <RadioGroup
          value={value?.text}
          options={choices}
          onChange={ev => handleChange(id, ev.target.value, type)}
        />
      );
      break;
    }
    case 'file': {
      const displayText = value?.text || value?.type || value;
      const hasValue = displayText && displayText !== 'link';
      component = (
        <FileContainer>
          <Button
            style={{ minWidth: '200px' }}
            onClick={() => {
              document.getElementById('input_file').click();
            }}
            variant="outlined"
          >
            Add File
          </Button>
          {invalidFile && <FileError>{invalidFile}</FileError>}
          {!invalidFile && !hasValue && (
            <FileText>Accepted file formats: DOC, PDF, XLSX</FileText>
          )}
          {!invalidFile && hasValue && (
            <>
              <FileSelected>File Selected: {displayText}</FileSelected>
              <DeleteIcon
                style={{ position: 'relative', right: -5, cursor: 'pointer' }}
                onClick={() => handleClearFile(questionId)}
              />
            </>
          )}
          <input
            style={{ display: 'none' }}
            id="input_file"
            type="file"
            onChange={ev => handleChange(id, ev.target.files[0], type)}
          />
          {uploading && <CircularProgress size="30px" />}
        </FileContainer>
      );
      break;
    }

    case 'upload_photo': {
      const launchPicker = () => {
        if (thumb) {
          handleSetError('Currently, you are allowed to attach just one photo');
        } else {
          document.getElementById('input_upload_photo').click();
        }
      };
      component = (
        <p>
          <FileContainer>
            <Button
              style={{ minWidth: '200px' }}
              onClick={launchPicker}
              variant="outlined"
            >
              {response}
            </Button>
            {invalidFile && <FileError>{invalidFile}</FileError>}
            {!invalidFile && !thumb && (
              <FileText>
                Accepted file formats: JPG, PNG, MP4, DOC, PDF
              </FileText>
            )}
            <input
              style={{ display: 'none' }}
              id="input_upload_photo"
              type="file"
              onChange={ev => handleChange(id, ev.target.files[0], type)}
            />
            {uploading && <CircularProgress size="30px" />}
            <br />
            {thumb && (
              <UploadBox>
                <DeleteIcon onClick={ev => handleClearThumb(ev, id, type)} />
                {thumb !== 'other' && (
                  <Preview
                    onClick={() => {
                      document.getElementById('input_upload_photo').click();
                    }}
                    style={{ backgroundImage: `url(${thumb})` }}
                  />
                )}
                {thumb === 'other' && <OtherFile>YOUR STORY FILE</OtherFile>}
              </UploadBox>
            )}
          </FileContainer>
        </p>
      );
      break;
    }
    case 'measured':
      component = (
        <MeasurementSelector
          value={initialValue}
          onChange={newValue => handleChange(id, newValue, type)}
          questionId={questionId}
        />
      );
      break;
    case 'currency_multi':
      component = (
        <CurrencyContainer>
          <CurrencyField
            value={initialValue?.currency}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FontAwesomeIcon icon={faDollarSign} />
                </InputAdornment>
              ),
            }}
            onChange={ev => handleChange(id, ev.target.value, type)}
          />
          {feExtraRequirement === 'help_budget' && (
            <TrueImpactText
              variant="body-link"
              className="help-calculate"
              onClick={() => handleHelpBudget()}
            >
              Help me calculate this
            </TrueImpactText>
          )}
          {feExtraRequirement === 'help_in_kind' && (
            <TrueImpactText
              variant="body-link"
              className="help-calculate"
              onClick={() => handleInKindDrawerPress()}
            >
              Help me calculate this
            </TrueImpactText>
          )}
          {feExtraRequirement === 'help_overhead' && (
            <TrueImpactText
              variant="body-link"
              className="help-calculate"
              onClick={() => handleOverheadDrawerPress()}
            >
              Help me calculate this
            </TrueImpactText>
          )}
        </CurrencyContainer>
      );
      break;
    case 'long_text_multi':
      component = (
        <SubContainer>
          <Text>{response}</Text>
          <TextArea
            value={initialValue?.text}
            onChange={ev => handleChange(id, ev.target.value, type)}
          />
        </SubContainer>
      );
      break;
    case 'categories_lesson':
      component = (
        <SubContainer>
          <Text style={{ marginBottom: '20px' }}>{response}</Text>
          <LessonSelector
            value={initialValue?.lesson}
            onChange={newValue => handleChange(id, newValue, type)}
            questionId={questionId}
          />
        </SubContainer>
      );
      break;
    default:
      component = null;
  }
  return component;
};

const CurrencyField = styled(TextField).attrs({
  type: 'number',
  min: 1,
  step: '.01',
})`
  max-width: 320px;
  margin-left: 10px !important;
  margin-right: 10px !important;
  margin-top: 0px !important;
  background-color: #ffffff !important;
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input {
    -moz-appearance: textfield;
  }
  .MuiInputBase-root {
    padding-left: 0;
  }
  .MuiInputAdornment-root {
    background-color: ${COLORS.greyBase};
    max-height: 40px;
    height: 40px;
    padding: 16px;
  }
`;
const CurrencyContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-bottom: solid 1px #ccc;
  margin-top: 15px;
  margin-bottom: 15px;
  padding-bottom: 35px;
  .help-calculate {
    cursor: pointer;
  }
`;
const NumberField = styled(TextField).attrs({
  fullWidth: true,
  type: 'number',
  min: 0,
  step: '.01',
})`
  margin-top: 20px !important;
  background-color: #ffffff !important;
`;
const Link = styled.span`
  cursor: pointer;
  text-decoration: underline;
  color: #0072a2;
  font-weight: 700;
  font-size: 17px;
`;
const TextArea = styled(TextField).attrs({
  fullWidth: true,
  multiline: true,
  rows: 10,
})`
  background-color: #ffffff !important;
`;
const FileContainer = styled.div`
  flex-direction: row;
`;
const FileText = styled.span`
  font-style: italic;
  font-size: 14px;
  font-weight: 400;
`;
const FileSelected = styled.span`
  color: #0072a2;
  font-style: italic;
  font-size: 14px;
  font-weight: 700;
`;
const FileError = styled.span`
  color: #f34747;
  font-style: italic;
  font-size: 14px;
  font-weight: 700;
`;
const SubContainer = styled.div`
  width: 100%;
  height: auto;
`;
const UploadBox = styled.div`
  position: relative;
  margin-top: 10px;
  width: 180px;
  height: 130px;
  background-color: #ebf0f6;
  border-radius: 12px;
  display: flex;
  text-align: center;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  svg {
    color: white;
    font-size: 55px;
  }
`;
const Text = styled.span`
  display: inline-block;
  font-weight: 400;
  font-size: 15px;
  margin-top: 20px;
`;
const Preview = styled.div`
  border-radius: 12px;
  background-size: cover;
  width: 100%;
  height: 100%;
`;
const DeleteIcon = styled(DeleteOutlineIcon)`
  color: #0072a2 !important;
  position: absolute;
  top: 5px;
  right: 5px;
  font-size: 20px !important;
`;
const DrawerContainer = styled.div`
  width: 100%;
  padding: ${SPACERS.large};
  background-color: #f9f9f9;
  height: 100%;
  span {
    font-size: 17px;
    color: #505050;
  }
  .t {
    margin-top: 20px;
    width: 100%;
    display: flex;
    flex-direction: column;
    margin-bottom: 40px;
    .r {
      flex: 1;
      display: flex;
      background-color: white;
      flex-direction: row;
      padding: 15px;
      border-top: solid 1px #ebebeb;
      border-left: solid 1px #ebebeb;
      border-rght: solid 1px #ebebeb;
      .c {
        flex: 1;
      }
    }
    .r.title {
      background-color: transparent !important;
      border-top: none !important;
      border-left: none !important;
      border-rght: none !important;
      .c {
        font-weight: 700;
      }
    }
    .r.first {
      border-top-left-radius: 4px;
      border-top-right-radius: 4px;
    }
    .r.last {
      background-color: #ebf0f6;
      border-bottom: solid 1px #ebebeb;
      border-bottom-left-radius: 4px;
      border-bottom-right-radius: 4px;
      .c {
        font-weight: 700;
      }
    }
  }
`;
const ButtonDrawer = styled(Button)`
  width: 100%;
  span {
    color: #ffffff;
  }
`;
const OtherFile = styled.span`
  color: #33404d;
  font-size: 18px;
  font-weight: bold;
  text-align: center;
`;
